import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, PenLine } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import {
    Command,
    CommandEmpty,
    CommandInput,
    CommandItem,
    CommandList,
} from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import type { CoAuthor } from '../../../services/api/policyService';

export interface DepartmentUser {
    _id: string;
    name: string;
    role?: string;
}

interface CoAuthorPickerProps {
    departmentUsers: DepartmentUser[];
    value: CoAuthor[];
    onChange: (value: CoAuthor[]) => void;
    loading?: boolean;
}

export function CoAuthorPicker({
    departmentUsers,
    value,
    onChange,
    loading,
}: CoAuthorPickerProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const selectedUserIds = new Set(
        value.filter((c) => c.user_id).map((c) => c.user_id)
    );

    const handleSelectUser = (user: DepartmentUser) => {
        if (selectedUserIds.has(user._id)) {
            if (value.length <= 1) return;
            onChange(value.filter((c) => c.user_id !== user._id));
        } else {
            onChange([...value, { user_id: user._id, name: user.name }]);
        }
    };

    const handleAddFreetext = () => {
        const trimmed = search.trim();
        if (!trimmed) return;
        const alreadyExists = value.some(
            (c) =>
                !c.user_id &&
                c.name?.toLowerCase() === trimmed.toLowerCase()
        );
        if (alreadyExists) return;
        onChange([...value, { name: trimmed }]);
        setSearch('');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            const trimmed = search.trim();
            if (!trimmed) return;
            const matchingUser = departmentUsers.find(
                (u) => u.name.toLowerCase() === trimmed.toLowerCase()
            );
            if (matchingUser) {
                handleSelectUser(matchingUser);
                setSearch('');
            } else {
                handleAddFreetext();
            }
            e.preventDefault();
        }
    };

    const filtered = departmentUsers.filter((u) =>
        u.name.toLowerCase().includes(search.toLowerCase())
    );

    const showFreetextOption =
        search.trim() &&
        !departmentUsers.some(
            (u) => u.name.toLowerCase() === search.trim().toLowerCase()
        );

    const authorNames = value
        .map((c) => c.name?.trim())
        .filter((name): name is string => !!name);
    const visibleSummary = authorNames.slice(0, 2).join(', ');
    const overflowCount = authorNames.length - 2;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    aria-label={t('departmentPolicies.authorPicker.selectAuthors')}
                    className={cn(
                        'w-full justify-between font-normal shadow-sm hover:bg-transparent focus:ring-1 focus:ring-ring h-9 px-3 py-1.5 text-sm leading-tight',
                        !value.length && 'text-muted-foreground',
                        loading && 'pointer-events-none opacity-60'
                    )}
                >
                    {value.length === 0 ? (
                        <span>{t('departmentPolicies.authorPicker.addAuthor')}</span>
                    ) : (
                        <span className="truncate text-left text-foreground">
                            {visibleSummary}
                            {overflowCount > 0 && (
                                <span className="text-muted-foreground">
                                    {` ${t('departmentPolicies.authorPicker.more', { count: overflowCount })}`}
                                </span>
                            )}
                        </span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                <Command shouldFilter={false}>
                    <CommandInput
                        placeholder={t('departmentPolicies.authorPicker.searchPlaceholder')}
                        value={search}
                        onValueChange={setSearch}
                        onKeyDown={handleKeyDown}
                    />
                    <CommandList>
                        {filtered.map((user) => (
                            <CommandItem
                                key={user._id}
                                value={user._id}
                                onSelect={() => handleSelectUser(user)}
                                className="hover:bg-accent hover:text-accent-foreground data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
                            >
                                <div
                                    className={cn(
                                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                                        selectedUserIds.has(user._id)
                                            ? 'bg-primary text-primary-foreground'
                                            : 'opacity-50'
                                    )}
                                >
                                    <Check
                                        className={cn(
                                            'h-3 w-3',
                                            selectedUserIds.has(user._id)
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                        )}
                                    />
                                </div>
                                <span className="truncate">{user.name}</span>
                            </CommandItem>
                        ))}
                        {showFreetextOption && (
                            <CommandItem
                                value={`freetext:${search.trim()}`}
                                onSelect={handleAddFreetext}
                                className="hover:bg-accent hover:text-accent-foreground data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
                            >
                                <PenLine className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                                {t('departmentPolicies.authorPicker.addAsAuthor', { name: search.trim() })}
                            </CommandItem>
                        )}
                        {!search.trim() && departmentUsers.length === 0 && (
                            <CommandEmpty>{t('departmentPolicies.authorPicker.noUsersFound')}</CommandEmpty>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
